import React, { useState, useRef, useEffect, useCallback } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { useToast } from '../../../lib/ToastContext'
import SignaturePad from './SignaturePad'
import { getCaptureMediaInfo } from '../lib/goldPurchaseSnapshot.mjs'

const CaptureFlow = ({
  items = [],
  customerName = '',
  // Pre-filled captures (e.g. the AI ID-scan photo reused as id_front) — the
  // matching step shows the photo already taken instead of opening the camera.
  initialCaptures,
  onComplete,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [captures, setCaptures] = useState(() => ({ ...(initialCaptures || {}) }))
  const [skippedSteps, setSkippedSteps] = useState({})
  const [cameraActive, setCameraActive] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(() => (initialCaptures || {}).id_front || null)
  const [cameraError, setCameraError] = useState(null)

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const cameraGenerationRef = useRef(0)
  const fileInputRef = useRef(null)
  const fileReaderRef = useRef(null)
  const uploadGenerationRef = useRef(0)
  const activeStepRef = useRef('id_front')
  const toast = useToast()

  // Build capture steps based on items
  const buildSteps = useCallback(() => {
    const steps = []
    const firstName = customerName.split(' ')[0] || 'Customer'

    // Step 1: ID Photo
    steps.push({
      id: 'id_front',
      type: 'photo',
      label: 'ID Photo',
      hint: 'Please take a photo of the customer ID.'
    })

    steps.push({
      id: 'customer_photo',
      type: 'photo',
      optional: true,
      label: 'Customer Photo (optional)',
      hint: 'Take a separate photo of the customer, or explicitly skip this step. This is not the ID photo.'
    })

    // Steps for each item photo
    items.forEach((item, index) => {
      const itemLabel = getItemLabel(item)
      steps.push({
        id: `item_photo_${index}`,
        type: 'photo',
        label: items.length > 1 ? `Item Photo ${index + 1}` : 'Item Photo',
        hint: `Please take a photo of the ${itemLabel}.`,
        itemLabel
      })
    })

    // If no items, add a generic item photo step
    if (items.length === 0) {
      steps.push({
        id: 'item_photo_0',
        type: 'photo',
        label: 'Item Photo',
        hint: 'Please take a photo of the item(s).'
      })
    }

    // Seller signature
    const itemDescription = items.length > 0
      ? `the ${items.map(getItemLabel).join(', ')}`
      : 'the items'

    steps.push({
      id: 'signature_seller',
      type: 'signature',
      label: 'Seller Signature',
      prompt: `Hey ${firstName}, please sign below to agree to the sale of ${itemDescription}.`,
      terms: 'By signing, the seller confirms the sale details.'
    })

    // Buyer signature
    steps.push({
      id: 'signature_buyer',
      type: 'signature',
      label: 'Buyer Signature',
      prompt: `Store representative, please sign to confirm the purchase of ${itemDescription}.`,
      terms: ''
    })

    return steps
  }, [items, customerName])

  const steps = buildSteps()
  const currentStepData = steps[currentStep]
  activeStepRef.current = currentStepData?.id
  const isLastStep = currentStep === steps.length - 1
  const isPhotoStep = currentStepData?.type === 'photo'
  const isSignatureStep = currentStepData?.type === 'signature'

  // Get a readable label for an item
  function getItemLabel(item) {
    if (!item) return 'Item'

    const metal = item.metal === 'silver' ? '99% Silver' : `${item.karatWhole || 18}.${item.karatDecimal || '00'}K Gold`
    const type = item.itemType ? capitalizeFirst(item.itemType) : ''

    if (type) {
      return `${metal} ${type}`
    }
    return metal
  }

  function capitalizeFirst(str) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // Start camera
  const startCamera = useCallback(async () => {
    if (streamRef.current) return true
    const generation = ++cameraGenerationRef.current
    let requestedStream = null

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: currentStepData?.id === 'customer_photo' ? 'user' : 'environment' } }
      })
      requestedStream = stream
      // A permission prompt can outlive Skip, Back, or the whole dialog.
      // Never attach a stale camera result; stop its tracks immediately.
      if (generation !== cameraGenerationRef.current) {
        stream.getTracks().forEach(track => track.stop())
        return false
      }

      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      if (generation !== cameraGenerationRef.current) {
        stream.getTracks().forEach(track => track.stop())
        if (streamRef.current === stream) streamRef.current = null
        return false
      }
      setCameraActive(true)
      setCameraError(null)
      return true
    } catch (err) {
      if (requestedStream) {
        requestedStream.getTracks().forEach(track => track.stop())
        if (streamRef.current === requestedStream) streamRef.current = null
      }
      if (generation !== cameraGenerationRef.current) return false
      console.warn('Camera access failed:', err)
      setCameraError('Camera access denied. Please use the upload button.')
      return false
    }
  }, [currentStepData?.id])

  // Stop camera
  const stopCamera = useCallback(() => {
    cameraGenerationRef.current += 1
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setCameraActive(false)
  }, [])

  // Effect to manage camera based on step
  useEffect(() => {
    if (isPhotoStep && !previewUrl) {
      startCamera()
    } else {
      stopCamera()
    }

    return () => stopCamera()
  }, [currentStep, isPhotoStep, previewUrl, startCamera, stopCamera])

  const cancelPendingUpload = useCallback(() => {
    uploadGenerationRef.current += 1
    const reader = fileReaderRef.current
    fileReaderRef.current = null
    if (reader?.readyState === 1) reader.abort()
  }, [])

  // File selection may still be reading when the operator navigates or skips.
  useEffect(() => cancelPendingUpload, [currentStepData?.id, cancelPendingUpload])

  const clearOptionalSkip = (stepId) => {
    setSkippedSteps(prev => {
      if (!prev[stepId]) return prev
      const next = { ...prev }
      delete next[stepId]
      return next
    })
  }

  // Capture photo from camera
  const capturePhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const ctx = canvas.getContext('2d')
    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 480

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    clearOptionalSkip(currentStepData.id)
    setPreviewUrl(dataUrl)
    setCaptures(prev => ({
      ...prev,
      [currentStepData.id]: dataUrl
    }))
  }

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const stepId = currentStepData.id
    clearOptionalSkip(stepId)
    const generation = ++uploadGenerationRef.current
    if (fileReaderRef.current?.readyState === 1) fileReaderRef.current.abort()

    const reader = new FileReader()
    fileReaderRef.current = reader
    reader.onload = () => {
      if (generation !== uploadGenerationRef.current || activeStepRef.current !== stepId) return
      const dataUrl = reader.result
      try {
        getCaptureMediaInfo(dataUrl)
      } catch {
        toast.warning('Please choose a JPEG or PNG image.')
        return
      }
      setPreviewUrl(dataUrl)
      setCaptures(prev => ({
        ...prev,
        [stepId]: dataUrl
      }))
    }
    reader.onerror = () => {
      if (generation === uploadGenerationRef.current && activeStepRef.current === stepId) {
        toast.warning('Could not read that image. Please choose another JPEG or PNG.')
      }
    }
    reader.readAsDataURL(file)
  }

  // Retake photo
  const retakePhoto = () => {
    clearOptionalSkip(currentStepData.id)
    setPreviewUrl(null)
    setCaptures(prev => {
      const updated = { ...prev }
      delete updated[currentStepData.id]
      return updated
    })
  }

  // Handle signature capture
  const handleSignature = (dataUrl) => {
    setCaptures(prev => ({
      ...prev,
      [currentStepData.id]: dataUrl
    }))
  }

  // Check if current step is complete
  const isStepComplete = () => {
    return !!captures[currentStepData?.id] || (currentStepData?.optional && !!skippedSteps[currentStepData.id])
  }

  const advance = (nextCaptures = captures, nextSkippedSteps = skippedSteps) => {
    stopCamera()
    cancelPendingUpload()
    if (isLastStep) {
      // Complete the flow
      if (onComplete) {
        onComplete(nextCaptures, { customerPhotoSkipped: !!nextSkippedSteps.customer_photo && !nextCaptures.customer_photo })
      }
    } else {
      // Restore an existing capture's preview when revisiting a step
      setPreviewUrl(nextCaptures[steps[currentStep + 1]?.id] || null)
      setCurrentStep(prev => prev + 1)
    }
  }

  // Optional media still requires an explicit choice; the ordinary Next button
  // does not silently omit the customer photo.
  const skipOptionalPhoto = () => {
    if (!currentStepData?.optional) return
    const nextCaptures = { ...captures }
    delete nextCaptures[currentStepData.id]
    const nextSkippedSteps = { ...skippedSteps, [currentStepData.id]: true }
    setCaptures(nextCaptures)
    setSkippedSteps(nextSkippedSteps)
    advance(nextCaptures, nextSkippedSteps)
  }

  const goNext = () => {
    if (!isStepComplete()) {
      toast.warning('Please complete this step before continuing.')
      return
    }
    advance()
  }

  // Go to previous step
  const goBack = () => {
    stopCamera()
    cancelPendingUpload()
    if (currentStep === 0) {
      if (onCancel) onCancel()
    } else {
      setPreviewUrl(captures[steps[currentStep - 1]?.id] || null)
      setCurrentStep(prev => prev - 1)
    }
  }

  return (
    <div className="space-y-4">
      {/* Progress indicator */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">
          Step {currentStep + 1} of {steps.length}
        </span>
        <div className="flex gap-1">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full ${
                idx <= currentStep ? 'bg-primary' : 'bg-muted/60'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step label */}
      <div className="text-center mb-4">
        <h3 className="font-semibold text-foreground">{currentStepData?.label}</h3>
        {isPhotoStep && (
          <p className="text-sm text-muted-foreground mt-1">{currentStepData?.hint}</p>
        )}
      </div>

      {/* Photo capture area */}
      {isPhotoStep && (
        <div className="space-y-4">
          <div className="relative aspect-[4/3] bg-foreground/90 rounded-xl overflow-hidden">
            {!previewUrl && (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            )}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Captured"
                className="w-full h-full object-cover"
              />
            )}
            {cameraError && !previewUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                <div className="text-center p-4">
                  <Icon name="CameraOff" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{cameraError}</p>
                </div>
              </div>
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          <div className="flex justify-center gap-3">
            {!previewUrl ? (
              <>
                <Button
                  variant="default"
                  onClick={capturePhoto}
                  iconName="Camera"
                  iconPosition="left"
                  disabled={!cameraActive}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Upload Photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  capture={currentStepData?.id === 'customer_photo' ? 'user' : 'environment'}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </>
            ) : (
              <Button
                variant="outline"
                onClick={retakePhoto}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retake Photo
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Signature area — keyed per step so the seller's signature can never
          bleed into the buyer's pad (fresh component per signature) */}
      {isSignatureStep && (
        <SignaturePad
          key={currentStepData.id}
          label={currentStepData.label}
          prompt={currentStepData.prompt}
          terms={currentStepData.terms}
          initialSignature={captures[currentStepData.id] || null}
          onSignature={handleSignature}
        />
      )}

      {/* Navigation buttons */}
      {currentStepData?.optional && (
        <Button variant="ghost" className="w-full" onClick={skipOptionalPhoto}>
          Skip customer photo
        </Button>
      )}
      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border/60">
        <Button
          variant="outline"
          size="lg"
          onClick={goBack}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Back
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={goNext}
          disabled={!isStepComplete()}
          iconName={isLastStep ? 'Check' : 'ArrowRight'}
          iconPosition="right"
        >
          {isLastStep ? 'Finish & Save' : 'Next'}
        </Button>
      </div>
    </div>
  )
}

export default CaptureFlow
